import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Box,
	Typography,
	Alert,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

function DocumentModal({ open, onClose, onSave, initialData = null }) {
	const [formData, setFormData] = useState({
		nome: "",
		dataExpedicao: "",
		dataValidade: "",
		arquivo: null,
		arquivoNome: "",
	});
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (initialData) {
			// Se tem arquivo existente (URL string), extrair nome ou usar "Arquivo anexado"
			let arquivoNome = initialData.arquivoNome || "";
			if (initialData.arquivo && typeof initialData.arquivo === "string" && !arquivoNome) {
				arquivoNome = "Arquivo anexado (existente)";
			}

			setFormData({
				nome: initialData.nome || "",
				dataExpedicao: initialData.dataExpedicao || "",
				dataValidade: initialData.dataValidade || "",
				arquivo: initialData.arquivo || null,
				arquivoNome: arquivoNome,
			});
		} else {
			setFormData({
				nome: "",
				dataExpedicao: "",
				dataValidade: "",
				arquivo: null,
				arquivoNome: "",
			});
		}
		setErrors({});
	}, [initialData, open]);

	const handleChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
		// Limpar erro do campo quando usuário começar a digitar
		if (errors[field]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};

	const handleFileChange = (event) => {
		const file = event.target.files[0];
		if (file) {
			// Validar tamanho do arquivo (máx 10MB)
			if (file.size > 10 * 1024 * 1024) {
				setErrors((prev) => ({
					...prev,
					arquivo: "O arquivo deve ter no máximo 10MB",
				}));
				return;
			}

			// Validar tipo do arquivo
			const allowedTypes = [
				"application/pdf",
				"image/jpeg",
				"image/jpg",
				"image/png",
			];
			if (!allowedTypes.includes(file.type)) {
				setErrors((prev) => ({
					...prev,
					arquivo: "Apenas arquivos PDF, JPG e PNG são permitidos",
				}));
				return;
			}

			setFormData((prev) => ({
				...prev,
				arquivo: file,
				arquivoNome: file.name,
			}));
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors.arquivo;
				return newErrors;
			});
		}
	};

	const validate = () => {
		const newErrors = {};

		if (!formData.nome.trim()) {
			newErrors.nome = "Nome do documento é obrigatório";
		}

		if (!formData.dataExpedicao) {
			newErrors.dataExpedicao = "Data de expedição é obrigatória";
		}

		if (!formData.dataValidade) {
			newErrors.dataValidade = "Data de validade é obrigatória";
		}

		// Validar se data de validade é maior que data de expedição
		if (formData.dataExpedicao && formData.dataValidade) {
			if (new Date(formData.dataValidade) < new Date(formData.dataExpedicao)) {
				newErrors.dataValidade =
					"Data de validade deve ser posterior à data de expedição";
			}
		}

		if (!formData.arquivo && !initialData) {
			newErrors.arquivo = "Arquivo é obrigatório";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) {
			return;
		}

		// TODO: Implementar upload para S3 aqui
		// Por enquanto, apenas retorna os dados do formulário
		const documentData = {
			id: initialData?.id || Date.now(),
			nome: formData.nome,
			dataExpedicao: formData.dataExpedicao,
			dataValidade: formData.dataValidade,
			arquivo: formData.arquivo,
			arquivoNome: formData.arquivoNome,
			// Quando implementar S3, adicionar:
			// arquivoUrl: uploadedUrl,
		};

		onSave(documentData);
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ bgcolor: "#1a9e8f", color: "white" }}>
				{initialData ? "Editar Documento" : "Adicionar Documento"}
			</DialogTitle>
			<DialogContent sx={{ mt: 2 }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
					<TextField
						fullWidth
						label="Nome do Documento"
						value={formData.nome}
						onChange={(e) => handleChange("nome", e.target.value)}
						error={!!errors.nome}
						helperText={errors.nome}
						placeholder="Ex: Alvará Sisagri 2025"
						InputLabelProps={{ style: { fontSize: "15px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
						required
					/>

					<Box sx={{ display: "flex", gap: 2 }}>
						<TextField
							fullWidth
							label="Data de expedição"
							type="date"
							value={formData.dataExpedicao}
							onChange={(e) => handleChange("dataExpedicao", e.target.value)}
							error={!!errors.dataExpedicao}
							helperText={errors.dataExpedicao}
							InputLabelProps={{
								shrink: true,
								style: { fontSize: "15px" },
							}}
							inputProps={{ style: { fontSize: "15px" } }}
							required
						/>

						<TextField
							fullWidth
							label="Data de validade"
							type="date"
							value={formData.dataValidade}
							onChange={(e) => handleChange("dataValidade", e.target.value)}
							error={!!errors.dataValidade}
							helperText={errors.dataValidade}
							InputLabelProps={{
								shrink: true,
								style: { fontSize: "15px" },
							}}
							inputProps={{ style: { fontSize: "15px" } }}
							required
						/>
					</Box>

					<Box>
						<input
							accept=".pdf,.jpg,.jpeg,.png"
							style={{ display: "none" }}
							id="file-upload"
							type="file"
							onChange={handleFileChange}
						/>
						<label htmlFor="file-upload">
							<Button
								variant="outlined"
								component="span"
								startIcon={<UploadFileIcon />}
								fullWidth
								sx={{
									height: "48px",
									textTransform: "none",
									borderColor: errors.arquivo ? "#d32f2f" : "#d0d0d0",
									color: errors.arquivo ? "#d32f2f" : "#666",
									fontSize: "15px",
									"&:hover": {
										borderColor: "#1a9e8f",
										color: "#1a9e8f",
										bgcolor: "rgba(23, 162, 184, 0.04)",
									},
								}}
							>
								Selecionar Arquivo
							</Button>
						</label>
						{formData.arquivoNome && (
							<Typography
								variant="body2"
								sx={{ mt: 1, color: "#1a9e8f", fontSize: "14px" }}
							>
								Arquivo selecionado: {formData.arquivoNome}
							</Typography>
						)}
						{errors.arquivo && (
							<Typography
								variant="caption"
								sx={{ mt: 0.5, color: "#d32f2f", fontSize: "12px", display: "block" }}
							>
								{errors.arquivo}
							</Typography>
						)}
						<Typography variant="caption" sx={{ mt: 1, color: "#666", display: "block" }}>
							Formatos aceitos: PDF, JPG, PNG (máx 10MB)
						</Typography>
					</Box>

					{Object.keys(errors).length > 0 && (
						<Alert severity="error" sx={{ fontSize: "14px" }}>
							Por favor, corrija os erros antes de salvar
						</Alert>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ p: 2 }}>
				<Button onClick={onClose} sx={{ color: "#666" }}>
					Cancelar
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					sx={{
						bgcolor: "#1a9e8f",
						"&:hover": {
							bgcolor: "#138a7d",
						},
					}}
				>
					Salvar
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default DocumentModal;
